import { webApiService } from '@/services'
import { Autocomplete, AutocompleteItem, Select, SelectItem, Spinner } from '@nextui-org/react'
import { enqueueSnackbar } from 'notistack'
import { FC, useEffect, useState } from 'react'

// Datos de la compra del cliente en WOM: el modelo que compró (del listado de getWomDeviceList) y su
// plan. El boost sale del propio modelo según el plan elegido (boost_w / boost_o / boost_m / boost_empresa / boost_sin_plan).
// El objeto completo del modelo viaja al backend al recibir, no solo el nombre.
export interface IWomPurchase {
  device: any | null
  plan: string
  boost: any
}

export const emptyWomPurchase: IWomPurchase = { device: null, plan: '', boost: null }

export const isWomPurchaseComplete = (purchase: IWomPurchase) => purchase.device !== null && purchase.plan !== ''

// Lo que viaja en device_purchased: el objeto del modelo tal cual vino del listado (con sku, los tres
// boosts, etc.) más el plan elegido y el boost que salió de ese plan.
export const womDevicePurchased = (purchase: IWomPurchase) => {
  if (purchase.device === null) return null
  return { ...purchase.device, plan: purchase.plan, extra_bono: purchase.boost }
}

// Las etiquetas son solo visuales (rangos de precio del plan); el mapeo al boost del modelo
// sigue siendo por Plan W / O / M / empresarial / sin plan.
const WOM_PLANS: Array<{ name: string, boostKey: string }> = [
  { name: 'Plan $4.990 a <$13.990', boostKey: 'boost_w' },
  { name: 'Plan $13.990 a <$18.990', boostKey: 'boost_o' },
  { name: 'Plan mayor a $18.990', boostKey: 'boost_m' },
  { name: 'Plan segmento empresas (PNG)', boostKey: 'boost_empresa' },
  { name: 'Sin Plan', boostKey: 'boost_sin_plan' }
]

// La opción "Otro" está disponible en todos los planes y siempre paga bono 0.
export const OTHER_WOM_DEVICE_KEY = 'otro'
const OTHER_WOM_DEVICE = { excel_name: 'Otro' }

const boostOf = (device: any | null, plan: string) => {
  const boostKey = WOM_PLANS.find((item) => item.name === plan)?.boostKey
  if (device === null || boostKey === undefined) return null
  return device[boostKey] ?? null
}

// Solo se listan los modelos que traen bono para el plan elegido: los que vienen en 0 (o sin dato) se ocultan.
const hasBoost = (device: any, plan: string) => {
  const boost = boostOf(device, plan)
  if (boost === null || boost === undefined || boost === '' || isNaN(Number(boost))) return false
  return Number(boost) !== 0
}

// El listado puede venir como strings (solo el nombre) o como objetos con más datos del modelo.
const normalize = (item: any) => (typeof item === 'object' && item !== null ? item : { excel_name: String(item) })

// El nombre a pintar. Si el objeto no trae excel_name se usa el primer texto que tenga, así el
// selector nunca queda con opciones en blanco si el backend cambia el nombre del campo.
export const womDeviceName = (device: any): string => {
  if (device === null || device === undefined) return ''
  if (typeof device !== 'object') return String(device)
  if (device.excel_name !== undefined && device.excel_name !== null && device.excel_name !== '') return String(device.excel_name)
  const firstText = Object.values(device).find((value) => typeof value === 'string' && value !== '')
  return firstText === undefined ? '' : String(firstText)
}

// WOM es solo Chile, así que el monto se muestra con formato chileno: "$ 25.000".
export const formatBoost = (boost: any) => {
  if (boost === null || boost === undefined || boost === '' || isNaN(Number(boost))) return 'sin dato'
  return `$ ${new Intl.NumberFormat('es-CL').format(Number(boost))}`
}

// En el listado de modelos se muestra "modelo - bono", donde el bono es el boost del plan ya elegido.
const womDeviceOptionLabel = (device: any, plan: string) => {
  const name = womDeviceName(device)
  if (plan === '') return name
  return `${name} - ${formatBoost(boostOf(device, plan))}`
}

const WomPurchaseFields: FC<{
  value: IWomPurchase
  onChange: (value: IWomPurchase) => void
  portalContainer?: HTMLElement | null
}> = ({ value, onChange, portalContainer }) => {
  const [devices, setDevices] = useState<any[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  // La key del autocomplete es la posición en el listado, así el objeto que viaja al backend queda tal cual vino.
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  useEffect(() => {
    const getDevices = async () => {
      try {
        setLoading(true)
        // El seller del listado es 'wom' fijo, no el slug de la url (pueden no coincidir).
        const res = await webApiService.getWomDeviceList('wom')
        console.log('res getWomDeviceList => ', res)
        const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.data?.data) ? res.data.data : []
        console.log('modelos WOM cargados => ', list.length)
        setDevices(list.map(normalize))
        if (list.length === 0) enqueueSnackbar('El listado de modelos WOM vino vacío', { variant: 'warning' })
      } catch (error) {
        console.log('error getWomDeviceList => ', error)
        enqueueSnackbar('No se pudo cargar el listado de modelos WOM', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }

    getDevices()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className='flex justify-center py-4'><Spinner label='Cargando modelos...' /></div>

  // Los modelos con bono para el plan elegido (conservando su índice original en `devices`) más la opción "Otro".
  const deviceOptions = value.plan === ''
    ? []
    : devices
      .map((device, index) => ({ device, index }))
      .filter(({ device }) => hasBoost(device, value.plan))
      .map(({ device, index }) => ({ id: String(index), name: womDeviceOptionLabel(device, value.plan) }))
      .concat([{ id: OTHER_WOM_DEVICE_KEY, name: `${womDeviceName(OTHER_WOM_DEVICE)} - ${formatBoost(0)}` }])

  return (
    <>
      <Select
        variant='bordered'
        label='Plan del cliente'
        placeholder='Seleccione el plan'
        selectedKeys={value.plan === '' ? [] : [value.plan]}
        popoverProps={{ portalContainer: portalContainer ?? undefined, placement: 'bottom' }}
        onChange={(e) => {
          const plan = e.target.value
          // Al cambiar el plan cambian los bonos de cada modelo, así que se obliga a elegir el modelo de nuevo.
          setSelectedKey(null)
          onChange({ device: null, plan, boost: null })
        }}
      >
        {WOM_PLANS.map((plan) => (
          <SelectItem key={plan.name} textValue={plan.name}>{plan.name}</SelectItem>
        ))}
      </Select>

      {/* El key fuerza a rearmar el listado al cambiar el plan, así los bonos que se muestran
          en cada opción corresponden siempre al plan elegido. */}
      <Autocomplete
        key={`wom-devices-${value.plan}`}
        variant='bordered'
        isDisabled={value.plan === ''}
        defaultItems={deviceOptions}
        label='Modelo comprado por el cliente'
        placeholder={value.plan === '' ? 'Seleccione primero el plan' : 'Buscar modelo'}
        selectedKey={selectedKey}
        // El alto se maneja con maxListboxHeight (el contenedor con scroll del propio Autocomplete).
        // Ponerle un max-h al listbox recorta la lista y deja modelos fuera de alcance.
        maxListboxHeight={320}
        itemHeight={44}
        popoverProps={{
          portalContainer: portalContainer ?? undefined,
          placement: 'bottom'
        }}
        onSelectionChange={(key) => {
          if (key === null) {
            setSelectedKey(null)
            onChange({ device: null, plan: value.plan, boost: null })
            return
          }
          setSelectedKey(String(key))
          if (String(key) === OTHER_WOM_DEVICE_KEY) {
            onChange({ device: OTHER_WOM_DEVICE, plan: value.plan, boost: 0 })
            return
          }
          const device = devices[Number(key)] ?? null
          onChange({ device, plan: value.plan, boost: boostOf(device, value.plan) })
        }}
      >
        {(item: any) => (
          <AutocompleteItem key={item.id} textValue={item.name}>
            {item.name}
          </AutocompleteItem>
        )}
      </Autocomplete>

      {value.device !== null && value.plan !== '' && (
        <div className='rounded-medium border border-default-200 p-3'>
          <p className='font-bold text-small'>Extra Bono calculado</p>
          <p className='text-small'>Extra Bono: <span className='font-bold'>{formatBoost(value.boost)}</span> ({value.plan})</p>
        </div>
      )}
    </>
  )
}

export default WomPurchaseFields
